import React, { useEffect, useState } from 'react';

const TOAST_CONFIGS = {
  approve: {
    bg: '#ECFDF5',
    border: '#D4A520',
    iconBg: '#D4A520',
    titleColor: '#065F46',
    msgColor: '#047857',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M12 2L13.09 8.26L19 7L15.45 12L19 17L13.09 15.74L12 22L10.91 15.74L5 17L8.55 12L5 7L10.91 8.26L12 2Z" fill="#0B1F3B" strokeWidth="0"/>
      </svg>
    ),
  },
  navy: {
    bg: '#EFF6FF',
    border: '#D4A520',
    iconBg: '#0B1F3B',
    titleColor: '#1E3A5F',
    msgColor: '#1E40AF',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2"/>
      </svg>
    ),
  },
  error: {
    bg: '#FEF2F2',
    border: '#DC2626',
    iconBg: '#DC2626',
    titleColor: '#991B1B',
    msgColor: '#B91C1C',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M12 8v5M12 15v1" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
};

function Toast({ toast, onRemove }) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 20);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setLeaving(true);
    setTimeout(() => onRemove(toast?.id), 300);
  };

  const config = TOAST_CONFIGS?.[toast?.type] || TOAST_CONFIGS?.navy;

  return (
    <div
      className="flex items-start gap-3 rounded-xl shadow-lg overflow-hidden"
      style={{
        background: config?.bg,
        borderLeft: `4px solid ${config?.border}`,
        maxWidth: 360,
        minWidth: 280,
        padding: '12px 14px',
        transform: visible && !leaving ? 'translateX(0)' : 'translateX(110%)',
        opacity: visible && !leaving ? 1 : 0,
        transition: 'transform 0.35s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease',
      }}
    >
      {/* Icon */}
      <div
        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5"
        style={{ background: config?.iconBg }}
      >
        {config?.icon}
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        {toast?.title && (
          <p className="text-sm font-bold leading-tight" style={{ color: config?.titleColor }}>
            {toast?.title}
          </p>
        )}
        <p className="text-xs leading-snug mt-0.5" style={{ color: config?.msgColor }}>
          {toast?.message}
        </p>
        {toast?.retryFn && (
          <button
            onClick={toast?.retryFn}
            className="mt-1.5 text-xs font-bold underline"
            style={{ color: config?.titleColor }}
          >
            Retry
          </button>
        )}
      </div>
      {/* Close */}
      <button
        onClick={dismiss}
        className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full hover:bg-black/10 transition-colors mt-0.5"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M8 2L2 8M2 2l6 6" stroke={config?.titleColor} strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
}

export default function ToastStack({ toasts, onRemove }) {
  if (!toasts?.length) return null;
  return (
    <div
      className="fixed top-5 right-5 z-[9999] flex flex-col gap-2 pointer-events-none"
      style={{ maxWidth: 380 }}
    >
      {toasts?.map(toast => (
        <div key={toast?.id} className="pointer-events-auto">
          <Toast toast={toast} onRemove={onRemove} />
        </div>
      ))}
    </div>
  );
}
